import {
  getNativeCapabilities,
  getNativeSessionEpoch,
  isInWebView,
  LEGACY_NATIVE_SESSION_PROTOCOL_VERSION,
  NATIVE_REFRESH_BROKER_PROTOCOL_VERSION,
  NATIVE_SESSION_MODE_HOST_ONLY_V1,
  postToNative,
} from "./nativeBridge";

export const NATIVE_REFRESH_RESULT_EVENT = "cc:native-refresh-result";
export const NATIVE_SESSION_BOOTSTRAP_EVENT = "cc_native_session_bootstrap";
const NATIVE_REFRESH_TIMEOUT_MS = 10_000;
const NATIVE_CAPABILITY_PROBE_TIMEOUT_MS = 1_500;
const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{16,80}$/;

export type NativeRefreshErrorCode =
  | "invalid_or_expired"
  | "token_reused"
  | "fingerprint_mismatch"
  | "session_unavailable"
  | "refresh_failed"
  | "persistence_failed"
  | "native_refresh_timeout"
  | "native_refresh_unavailable";

export type NativeRefreshAccess = {
  accessToken: string;
  expiresAt?: number;
};

export class NativeRefreshBrokerError extends Error {
  readonly code: NativeRefreshErrorCode;

  constructor(code: NativeRefreshErrorCode) {
    super(code);
    this.name = "NativeRefreshBrokerError";
    this.code = code;
  }
}

export function supportsNativeRefreshBroker(): boolean {
  const capabilities = getNativeCapabilities();
  return Boolean(
    isInWebView() &&
      capabilities.protocolVersion === NATIVE_REFRESH_BROKER_PROTOCOL_VERSION &&
      capabilities.supportsNativeRefreshBroker === true &&
      capabilities.nativeSessionMode === NATIVE_SESSION_MODE_HOST_ONLY_V1 &&
      getNativeSessionEpoch() !== undefined
  );
}

/**
 * The immediately preceding RN protocol explicitly advertised a native
 * session bundle and epoch, but not native refresh ownership. Absence of any
 * capability is not a legacy signal: it can also mean that bootstrap is late.
 */
export function supportsExplicitLegacyRefreshBridge(): boolean {
  const capabilities = getNativeCapabilities();
  return Boolean(
    isInWebView() &&
      capabilities.protocolVersion === LEGACY_NATIVE_SESSION_PROTOCOL_VERSION &&
      capabilities.supportsSessionBundle === true &&
      capabilities.supportsNativeRefreshBroker !== true &&
      getNativeSessionEpoch() !== undefined
  );
}

function createRequestId(): string {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (!cryptoApi || typeof cryptoApi.getRandomValues !== "function") {
    throw new NativeRefreshBrokerError("native_refresh_unavailable");
  }

  const bytes = new Uint8Array(24);
  cryptoApi.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

let inFlightRefresh: Promise<NativeRefreshAccess> | null = null;

let inFlightCapabilityProbe: Promise<boolean> | null = null;

/**
 * New RN versions answer this secret-free probe by repeating the document
 * bootstrap injection. Callers may use an explicit preceding-protocol
 * bootstrap as legacy evidence; silence is never a downgrade signal.
 */
export function waitForNativeRefreshBrokerCapability(
  timeoutMs = NATIVE_CAPABILITY_PROBE_TIMEOUT_MS
): Promise<boolean> {
  if (supportsNativeRefreshBroker()) {
    return Promise.resolve(true);
  }
  if (supportsExplicitLegacyRefreshBridge()) {
    return Promise.resolve(false);
  }
  if (!isInWebView() || typeof window === "undefined") {
    return Promise.resolve(false);
  }
  if (inFlightCapabilityProbe) {
    return inFlightCapabilityProbe;
  }

  let requestId: string;
  try {
    requestId = createRequestId();
  } catch {
    return Promise.resolve(false);
  }

  const boundedTimeout = Math.max(100, Math.min(10_000, timeoutMs));
  const probe = new Promise<boolean>((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      window.removeEventListener(
        NATIVE_SESSION_BOOTSTRAP_EVENT,
        handleBootstrap as EventListener
      );
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };

    const finish = (supported: boolean) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(supported);
    };

    const handleBootstrap = () => {
      if (
        supportsNativeRefreshBroker() ||
        supportsExplicitLegacyRefreshBridge()
      ) {
        finish(supportsNativeRefreshBroker());
      }
    };

    window.addEventListener(
      NATIVE_SESSION_BOOTSTRAP_EVENT,
      handleBootstrap as EventListener
    );
    timeoutId = setTimeout(
      () => finish(supportsNativeRefreshBroker()),
      boundedTimeout
    );

    if (!postToNative(
      "native_capability_probe",
      { requestId },
      { includeSessionEpoch: false }
    )) {
      finish(false);
    }
  });

  inFlightCapabilityProbe = probe.finally(() => {
    inFlightCapabilityProbe = null;
  });
  return inFlightCapabilityProbe;
}

export function requestNativeRefreshAccess(): Promise<NativeRefreshAccess> {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  if (!supportsNativeRefreshBroker()) {
    return Promise.reject(
      new NativeRefreshBrokerError("native_refresh_unavailable")
    );
  }

  const epoch = getNativeSessionEpoch();
  if (epoch === undefined) {
    return Promise.reject(
      new NativeRefreshBrokerError("native_refresh_unavailable")
    );
  }

  let requestId: string;
  try {
    requestId = createRequestId();
  } catch (error) {
    return Promise.reject(error);
  }

  const request = new Promise<NativeRefreshAccess>((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      window.removeEventListener(
        NATIVE_REFRESH_RESULT_EVENT,
        handleResult as EventListener
      );
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };

    const finish = (
      callback: () => void
    ) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    };

    const handleResult = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail;
      if (!detail || detail.requestId !== requestId || detail.epoch !== epoch) {
        return;
      }

      const currentEpoch = getNativeSessionEpoch();
      if (currentEpoch !== epoch) {
        return;
      }

      const error = String(detail.error || "");
      if (
        error === "invalid_or_expired" ||
        error === "token_reused" ||
        error === "fingerprint_mismatch" ||
        error === "session_unavailable" ||
        error === "refresh_failed" ||
        error === "persistence_failed"
      ) {
        finish(() => reject(new NativeRefreshBrokerError(error)));
        return;
      }

      const accessToken =
        typeof detail.accessToken === "string" ? detail.accessToken.trim() : "";
      if (!accessToken || accessToken.length > 16_384) {
        finish(() =>
          reject(new NativeRefreshBrokerError("refresh_failed"))
        );
        return;
      }

      const expiresAt =
        typeof detail.expiresAt === "number" &&
        Number.isFinite(detail.expiresAt) &&
        detail.expiresAt > Date.now()
          ? Math.floor(detail.expiresAt)
          : undefined;
      finish(() => resolve({ accessToken, expiresAt }));
    };

    window.addEventListener(
      NATIVE_REFRESH_RESULT_EVENT,
      handleResult as EventListener
    );
    timeoutId = setTimeout(() => {
      finish(() =>
        reject(new NativeRefreshBrokerError("native_refresh_timeout"))
      );
    }, NATIVE_REFRESH_TIMEOUT_MS);

    if (!REQUEST_ID_PATTERN.test(requestId) || !postToNative(
      "native_refresh_request",
      { requestId }
    )) {
      finish(() =>
        reject(new NativeRefreshBrokerError("native_refresh_unavailable"))
      );
    }
  });

  inFlightRefresh = request.finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}
